<template>
  <section class="cw-panel cw-stack cw-space" aria-labelledby="cw-import-title" :aria-busy="loading || saving">
    <div class="cw-row cw-between">
      <div><span class="cw-meta">Angebote übernehmen · {{ studio.name }}</span><h3 id="cw-import-title">Vertragsvorlagen mit V2 abgleichen</h3></div>
      <button class="cw-button" type="button" :disabled="loading || saving" @click="$emit('close')">Schliessen</button>
    </div>
    <p class="cw-small">Hier siehst du die veröffentlichten Angebote dieses Studios mit ihren Originalnamen, Leistungen und Preisen. Nach deiner Prüfung werden sie im eigenen V2-Katalog gespeichert.</p>
    <p v-if="loading" class="cw-small" role="status">Mitgliedschaften, Zusatzpakete und Preise werden geprüft …</p>
    <div v-if="error" class="cw-error" role="alert">{{ error }}</div>
    <p v-if="pending && !saving" class="cw-note">Die Antwort auf die Übernahme ist ausgeblieben. Mit «Übernahme erneut prüfen» wird dieselbe Anfrage wiederholt.</p>
    <template v-if="preview && !loading">
      <div class="cw-import-stats">
        <div><strong>{{ preview.summary.templates }}</strong><span class="cw-small">Vorlagen</span></div>
        <div><strong>{{ preview.summary.memberships }}</strong><span class="cw-small">Mitgliedschaften</span></div>
        <div><strong>{{ preview.summary.packages }}</strong><span class="cw-small">Zusatzpakete</span></div>
      </div>
      <p v-if="!preview.hasChanges" class="cw-notice" role="status">Die veröffentlichten Angebote stimmen mit dem V2-Katalog überein. Es gibt keine Änderungen zu übernehmen.</p>
      <template v-else>
        <div class="cw-row"><span class="cw-badge cw-badge-success">{{ preview.summary.added }} neue Angaben</span><span class="cw-badge">{{ preview.summary.changed }} Änderungen</span><span v-if="preview.summary.removed" class="cw-badge cw-badge-warning">{{ preview.summary.removed }} entfallende Angaben</span></div>
        <details v-if="priceChanges.length" open>
          <summary>Preise prüfen · {{ priceChanges.length }} Angaben</summary>
          <div class="cw-import-table cw-top-space" tabindex="0" role="region" aria-label="Preisvergleich">
            <table class="cw-table"><thead><tr><th scope="col">Angebot / Zusatzpaket</th><th scope="col">Bisher in V2</th><th scope="col">Nach Übernahme</th></tr></thead><tbody>
              <tr v-for="change in priceChanges" :key="change.id"><td><span class="cw-meta cw-block">{{ change.template }} · {{ stateName(change.state) }}</span>{{ change.label }}</td><td>{{ value(change.before) }}</td><td>{{ value(change.after) }}</td></tr>
            </tbody></table>
          </div>
        </details>
        <details v-if="configChanges.length">
          <summary>Leistungen, Bedingungen und Zuordnungen · {{ configChanges.length }} Angaben</summary>
          <div class="cw-stack cw-top-space">
            <article v-for="change in configChanges" :key="change.id" class="cw-import-change">
              <span class="cw-meta">{{ change.template }} · {{ stateName(change.state) }}</span><h3>{{ change.label }}</h3>
              <div class="cw-import-pair cw-top-space"><div tabindex="0"><span class="cw-meta">Bisher in V2</span><p class="cw-small cw-prose">{{ value(change.before) }}</p></div><div tabindex="0"><span class="cw-meta">Nach Übernahme</span><p class="cw-small cw-prose">{{ value(change.after) }}</p></div></div>
            </article>
          </div>
        </details>
      </template>
      <p v-if="preview.summary.blockedOffers || preview.summary.warningCount" class="cw-note">{{ preview.summary.blockedOffers }} Angebote bleiben gesperrt. {{ preview.summary.warningCount }} Konfigurationshinweise findest du bei den betreffenden Vorlagen in der Vorschau.</p>
      <details><summary>Alle Angebote nach der Übernahme ansehen</summary><contract-catalog class="cw-top-space" :catalog="{ templates: preview.templates }" :can-create="false"></contract-catalog></details>
      <template v-if="preview.hasChanges">
        <p class="cw-small">Die Übernahme gilt für {{ studio.name }}. Bereits gespeicherte Verträge behalten ihre vereinbarten Daten. Die bisherigen Vertragseinstellungen werden nicht verändert.</p>
        <label class="cw-check"><input v-model="reviewed" type="checkbox" :disabled="saving || !!pending || stale">Ich habe die Änderungen für {{ studio.name }} geprüft.</label>
        <label v-if="preview.summary.removed" class="cw-check"><input v-model="acceptRemovals" type="checkbox" :disabled="saving || !!pending || stale">Die entfallenden Angaben sollen aus dem aktuellen V2-Angebot entfernt werden.</label>
      </template>
    </template>
    <div class="cw-row cw-between">
      <button class="cw-button" type="button" :disabled="loading || saving || !!pending" @click="load">Daten erneut prüfen</button>
      <button v-if="preview && preview.hasChanges" class="cw-button cw-primary" type="button" :disabled="!canApply" @click="apply">{{ saving ? 'Wird übernommen …' : pending ? 'Übernahme erneut prüfen' : 'In V2 übernehmen' }}</button>
    </div>
  </section>
</template>

<script>
import ContractCatalog from "./ContractCatalog.vue"
import api, { errorMessage } from "@/api/contractWorkspaceApi"

export default {
  name: "ContractImport",
  components: { ContractCatalog },
  props: { studio: { type: Object, required: true } },
  data: () => ({ preview: null, loading: false, saving: false, error: "", reviewed: false, acceptRemovals: false, stale: false, pending: null, loadId: 0 }),
  computed: {
    priceChanges() { return this.preview ? this.preview.changes.filter((entry) => entry.kind === "price") : [] },
    configChanges() { return this.preview ? this.preview.changes.filter((entry) => entry.kind !== "price") : [] },
    canApply() { return !this.loading && !this.saving && !this.stale && !!this.preview && this.preview.hasChanges && (!!this.pending || (this.reviewed && (!this.preview.summary.removed || this.acceptRemovals))) },
  },
  created() { this.load() },
  beforeDestroy() { this.loadId += 1 },
  methods: {
    value(text) { return text === null || text === "" ? "Nicht vorhanden" : text },
    stateName(state) { return { added: "Neu", changed: "Geändert", removed: "Entfällt" }[state] },
    async load() {
      if (this.loading || this.saving || this.pending) return
      const loadId = ++this.loadId
      this.loading = true; this.error = ""; this.preview = null; this.reviewed = false; this.acceptRemovals = false; this.stale = false; this.$emit("busy", true)
      try { const result = await api.previewImport(this.studio.id); if (loadId === this.loadId) this.preview = result }
      catch (error) { if (loadId === this.loadId) this.error = errorMessage(error) }
      finally { if (loadId === this.loadId) { this.loading = false; this.$emit("busy", false) } }
    },
    async apply() {
      if (!this.canApply) return
      this.saving = true; this.error = ""; this.$emit("busy", true)
      const pending = this.pending || { id: this.preview.importId, data: { studioId: this.studio.id, reviewed: this.reviewed, acceptRemovals: this.acceptRemovals } }
      this.pending = pending
      try {
        const result = await api.applyImport(pending.id, pending.data)
        this.pending = null; this.$emit("dirty", false); this.$emit("imported", result)
      } catch (error) {
        this.error = errorMessage(error)
        if (error.response && error.response.status < 500) { this.pending = null; this.stale = true }
        this.$emit("dirty", !!this.pending)
      } finally { this.saving = false; this.$emit("busy", false) }
    },
  },
}
</script>
